import React, { useEffect, useState } from 'react';
import { getMobileToken } from '../../agent/mobileClient';
import './chat-content.css';

export interface LinkPreview {
  url: string;
  title: string;
  description: string;
  siteName: string;
  image: string;
}

const cache = new Map<string, LinkPreview | null>();

export function LinkPreviewCard({ url }: { url: string }) {
  const [preview, setPreview] = useState<LinkPreview | null | undefined>(cache.get(url));
  useEffect(() => {
    if (cache.has(url)) { setPreview(cache.get(url) ?? null); return; }
    let cancelled = false;
    const token = getMobileToken();
    fetch(`/api/agent/link-preview?url=${encodeURIComponent(url)}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (res) => (res.ok ? (await res.json()).preview as LinkPreview : null))
      .then((data) => {
        cache.set(url, data);
        if (!cancelled) setPreview(data);
      })
      .catch(() => { cache.set(url, null); if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [url]);
  if (preview === undefined) return <div className="chat-link-preview is-loading">正在预览链接…</div>;
  if (preview === null) return null;
  return (
    <a className="chat-link-preview" href={preview.url} target="_blank" rel="noreferrer noopener">
      {preview.image && <img src={preview.image} alt="" loading="lazy" />}
      <span>
        <strong>{preview.title}</strong>
        {preview.description && <small>{preview.description}</small>}
        <em>{preview.siteName}</em>
      </span>
    </a>
  );
}

const URL_RE = /https?:\/\/[^\s<>"']+/g;

function cleanUrl(raw: string): string {
  return raw.replace(/[.,，。！？；：、)）\]】}>]+$/u, '');
}

export function LinkifiedText({ text }: { text: string }) {
  const urls = Array.from(new Set((text.match(URL_RE) ?? []).map(cleanUrl))).slice(0, 3);
  if (urls.length === 0) return <>{text}</>;
  const parts = text.split(URL_RE);
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < parts.length - 1 && (() => {
            const raw = text.match(URL_RE)?.[index];
            const url = raw ? cleanUrl(raw) : '';
            return url ? <a href={url} target="_blank" rel="noreferrer noopener">{raw}</a> : null;
          })()}
        </React.Fragment>
      ))}
      <span className="chat-link-previews">
        {urls.map((url) => <LinkPreviewCard key={url} url={url} />)}
      </span>
    </>
  );
}
